import type { ReactElement } from "react";
import { Group as KonvaGroup, Rect as KonvaRect } from "react-konva";
import { SocialPostSceneBlockContent } from "@runcheetah/ui";

import type { SceneBlock, SceneBrandkit, SocialPostGenerationTaskDetail } from "../types";

type ImageAsset = Pick<
  SocialPostGenerationTaskDetail["linkedAssets"][number],
  | "generatedAssetId"
  | "blockId"
  | "role"
  | "position"
  | "objectKey"
  | "mimeType"
  | "width"
  | "height"
  | "prompt"
  | "signedUrl"
  | "createdAt"
>;

type LeafSceneBlock = Exclude<SceneBlock, { type: "group" }>;

export type KonvaRenderContext = {
  scale: number;
  selectedBlockId: string | null;
  imageAssetByBlockId: Map<string, ImageAsset>;
  brandkit: SceneBrandkit;
  onSelectBlock: (blockId: string) => void;
};

function SelectionOutline({
  block,
  context,
  persistent = false,
}: {
  block: SceneBlock;
  context: KonvaRenderContext;
  persistent?: boolean;
}) {
  const isSelected = context.selectedBlockId === block.id;

  if (!isSelected && !persistent) {
    return null;
  }

  return (
    <KonvaRect
      x={0}
      y={0}
      width={block.width * context.scale}
      height={block.height * context.scale}
      stroke={isSelected ? "#2563eb" : "rgba(100,116,139,0.8)"}
      strokeWidth={isSelected ? 2 : 1}
      dash={persistent ? [8, 6] : [6, 4]}
      cornerRadius={block.type === "rect" ? block.cornerRadius * context.scale : 0}
      listening={false}
    />
  );
}

function LeafBlockNode({ block, context }: { block: LeafSceneBlock; context: KonvaRenderContext }) {
  const selectBlock = () => context.onSelectBlock(block.id);

  return (
    <KonvaGroup
      x={block.x * context.scale}
      y={block.y * context.scale}
      opacity={block.opacity}
      rotation={block.rotation}
      onClick={selectBlock}
      onTap={selectBlock}
    >
      <KonvaRect
        x={0}
        y={0}
        width={block.width * context.scale}
        height={block.height * context.scale}
        fill="rgba(0,0,0,0.001)"
        strokeEnabled={false}
      />
      <SocialPostSceneBlockContent
        block={block}
        scale={context.scale}
        imageAssetByBlockId={context.imageAssetByBlockId}
        brandkit={
          block.type === "logo" && context.brandkit.logo?.objectKey !== block.assetObjectKey
            ? { ...context.brandkit, logo: null }
            : context.brandkit
        }
      />
      <SelectionOutline block={block} context={context} />
    </KonvaGroup>
  );
}

function GroupBlockNode({
  block,
  context,
}: {
  block: Extract<SceneBlock, { type: "group" }>;
  context: KonvaRenderContext;
}) {
  const selectBlock = () => context.onSelectBlock(block.id);

  return (
    <KonvaGroup
      x={block.x * context.scale}
      y={block.y * context.scale}
      opacity={block.opacity}
      rotation={block.rotation}
      clipX={block.clip ? 0 : undefined}
      clipY={block.clip ? 0 : undefined}
      clipWidth={block.clip ? block.width * context.scale : undefined}
      clipHeight={block.clip ? block.height * context.scale : undefined}
      onClick={selectBlock}
      onTap={selectBlock}
    >
      {block.children
        .toSorted((a, b) => a.zIndex - b.zIndex)
        .map((child) => (
          <BlockNode key={child.id} block={child as SceneBlock} context={context} />
        ))}
      <SelectionOutline block={block} context={context} persistent />
    </KonvaGroup>
  );
}

export function BlockNode({
  block,
  context,
}: {
  block: SceneBlock;
  context: KonvaRenderContext;
}): ReactElement | null {
  if (!block.visible) {
    return null;
  }

  if (block.type === "group") {
    return <GroupBlockNode block={block} context={context} />;
  }

  return <LeafBlockNode block={block} context={context} />;
}
